import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { get } from "../lib/http";
import { API } from "../config/api";
import * as XLSX from "xlsx";

/* -------------------- tiny helpers -------------------- */
const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const cn = (...a) => a.filter(Boolean).join(" ");
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const sum = (arr, key) => arr.reduce((acc, x) => acc + Number(x?.[key] || 0), 0);
const formatDDMonYYYY = (value) => {
  if (!value) return "–";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d)) return "–";
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = month[d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd}/${mon}/${yyyy}`;
};
const formatDDMonYYYY_HHMM = (value) => {
  if (!value) return "–";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d)) return "–";
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = month[d.getMonth()];
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mon}/${yyyy} ${hh}:${mm}`;
};

/* -------------------- UI atoms -------------------- */
function KPIBlock({ label, primary, hint = null }) {
  return (
    <div className="rounded-xl border bg-white dark:bg-gray-900 dark:border-gray-800 p-4 shadow-sm">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">{primary}</div>
      {hint && <div className="mt-1 text-xs text-emerald-700">{hint}</div>}
    </div>
  );
}
function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (Number(value) / Number(max)) * 100)) : 0;
  return (
    <div className="h-2 w-full bg-slate-200 dark:bg-gray-800 rounded-full overflow-hidden">
      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
    </div>
  );
}
function Th({ children, className = "" }) {
  return <th className={cn("py-3 px-4 font-semibold", className)}>{children}</th>;
}
function Td({ children, className = "", ...rest }) {
  return (
    <td className={cn("py-3 px-4 align-middle", className)} {...rest}>
      {children}
    </td>
  );
}

/* -------------------- small helpers for Guests UI -------------------- */
const getInitials = (s) => {
  if (!s) return "?";
  const parts = String(s).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text || "");
    // Optionally show a toast/tooltip in your app
  } catch (e) {
    // ignore
  }
};

/* -------------------- normalizers (server-shape agnostic) -------------------- */
function normalizeStatsResponse(res) {
  const root = res?.data && res.success === undefined ? res.data : res; // allow {success,data:{...}} or direct
  const data = root?.data || root?.stats || root;

  const event = data?.event || { id: data?.eventId, name: data?.eventName };
  const totals = data?.totals || {
    totalTickets: Number(data?.totalTickets ?? 0),
    sold: Number(data?.sold ?? 0),
    remaining: Number(
      data?.remaining ?? Math.max(0, (data?.totalTickets || 0) - (data?.sold || 0))
    ),
    revenue: Number(data?.revenue ?? 0),
  };

  let categories = [];
  if (Array.isArray(data?.categories)) {
    categories = data.categories.map((c) => ({
      id: c.id || c.categoryId || c.name,
      name: c.name || c.categoryId || "—",
      price: Number(c.price ?? 0),
      total: Number(c.total ?? c.totalTickets ?? 0),
      sold: Number(c.sold ?? 0),
      remaining: Number(
        c.remaining ?? Math.max(0, (c.total ?? c.totalTickets ?? 0) - (c.sold ?? 0))
      ),
      revenue: Number(c.revenue ?? Number(c.sold ?? 0) * Number(c.price ?? 0)),
    }));
  } else if (Array.isArray(data?.ticketCategories)) {
    categories = data.ticketCategories.map((c) => ({
      id: c.key || c.type || c.categoryId,
      name: c.type || c.categoryId || "—",
      price: Number(c.price ?? 0),
      total: Number(c.finalQuantity ?? c.quantity ?? 0),
      sold: Number(c.sold ?? 0),
      remaining: Math.max(0, Number(c.finalQuantity ?? c.quantity ?? 0) - Number(c.sold ?? 0)),
      revenue: Number(c.sold ?? 0) * Number(c.price ?? 0),
    }));
  }

  const derivedTotals = {
    totalTickets: totals.totalTickets || sum(categories, "total"),
    sold: totals.sold || sum(categories, "sold"),
    remaining:
      totals.remaining ||
      Math.max(
        0,
        (totals.totalTickets || sum(categories, "total")) -
          (totals.sold || sum(categories, "sold"))
      ),
    revenue: totals.revenue || sum(categories, "revenue"),
  };

  return { event, totals: derivedTotals, categories, eventMeta: data?.eventMeta };
}

function normalizeBookingsResponse(res) {
  const root = res?.data && res.success === undefined ? res.data : res;
  const arr = Array.isArray(root?.bookings)
    ? root.bookings
    : Array.isArray(root)
    ? root
    : Array.isArray(res?.bookings)
    ? res.bookings
    : [];

  const norm = arr.map((b) => {
    const id = b.id || b._id || b.bookingId || b.code || b.bookingCode || cryptoRandomKey();
    const code = b.bookingCode || b.code || b.booking_code || b.reference || "—";
    const time = b.bookingTime || b.createdAt || b.created_at || b.time || b.date || null;

    const qty =
      b.qty ??
      b.quantity ??
      b.ticketQty ??
      b.tickets ??
      (Array.isArray(b.items)
        ? b.items.reduce((n, it) => n + Number(it?.qty || it?.quantity || 0), 0)
        : 0);

    const amount =
      Number(
        b.amount ??
          b.total ??
          b.totalAmount ??
          b.price ??
          (Array.isArray(b.items)
            ? b.items.reduce(
                (sum, it) =>
                  sum +
                  Number(
                    it?.amount ||
                      Number(it?.qty || it?.quantity || 0) * Number(it?.price || 0) ||
                      0
                  ),
                0
              )
            : 0)
      ) || 0;

    let cat =
      b.ticketCategory ||
      b.category ||
      b.type ||
      (Array.isArray(b.items)
        ? b.items.map((it) => it?.category || it?.type || it?.name).filter(Boolean).join(", ")
        : "");
    if (!cat && Array.isArray(b.tickets)) {
      cat = b.tickets
        .map((t) => t?.category || t?.type || t?.name)
        .filter(Boolean)
        .join(", ");
    }

    const status = String(b.status || b.paymentStatus || b.bookingStatus || "unknown").toLowerCase();

    // ---- NEW: guestDetails pass-through as ARRAY (gestDetails fallback) ----
    const gdRaw = b.guestDetails ?? b.gestDetails ?? [];
    const guestDetails = Array.isArray(gdRaw) ? gdRaw : gdRaw ? [gdRaw] : [];
    const guestCount = guestDetails.length;

    return {
      id,
      code,
      time,
      qty: Number(qty || 0),
      amount: Number(amount || 0),
      category: cat || "—",
      status,
      guestDetails, // always array
      guestCount,
      raw: b,
    };
  });

  norm.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
  return norm;
}

function cryptoRandomKey() {
  try {
    const bytes = (window.crypto || window.msCrypto).getRandomValues(new Uint8Array(8));
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return String(Math.random()).slice(2);
  }
}

/* -------------------- Export XLSX (flatten guests + guests sheet) -------------------- */
function exportXLSX(rows, filename = "bookings.xlsx", guestCap = 8) {
  if (!Array.isArray(rows) || rows.length === 0) {
    alert("No bookings to export");
    return;
  }

  // determine maximum guests across rows up to guestCap
  const maxGuests = Math.min(
    guestCap,
    rows.reduce((m, b) => Math.max(m, Array.isArray(b.guestDetails) ? b.guestDetails.length : 0), 0)
  );

  // Build header for Bookings sheet
  const bookingHeaders = [
    "Booking Code",
    "Booking Time",
    "Donor Pass Qty",
    "Amount (INR)",
    "Donor Pass Category",
    "Booking Status",
    // "User Name",
    // "User Email",
    // "User Mobile",
    // "Payment Mode",
    // "Payment Status",
    // "Transaction Id",
  ];

  // Add Guest columns: Guest 1 Name, Guest 1 Email, ...
  for (let i = 1; i <= maxGuests; i++) {
    bookingHeaders.push(`Guest ${i} Name`);
    bookingHeaders.push(`Guest ${i} Email`);
    bookingHeaders.push(`Guest ${i} Phone`);
    bookingHeaders.push(`Guest ${i} CompanyName`);
    bookingHeaders.push(`Guest ${i} Designation`);
  }

  // Build rows for Bookings sheet
  const bookingRows = rows.map((b) => {
    const base = {
      "Booking Code": b.code || "",
      "Booking Time": b.time ? new Date(b.time).toISOString() : "",
      "Donor Pass Qty": b.qty ?? "",
      "Amount (INR)": typeof b.amount === "number" ? b.amount : "",
      "Donor Pass Category": b.category || "",
      "Booking Status": b.status || "",
      "User Name": b.user?.name || b.buyer?.name || "",
      "User Email": b.user?.email || b.buyer?.email || "",
      "User Mobile": b.user?.mobile || b.buyer?.mobile || "",
      "Payment Mode": b.paymentMode || "",
      "Payment Status": b.paymentStatus || "",
      "Transaction Id": b.transactionId || "",
    };

    const guestDetails = Array.isArray(b.guestDetails) ? b.guestDetails : [];

    for (let i = 0; i < maxGuests; i++) {
      const g = guestDetails[i] || {};
      base[`Guest ${i + 1} Name`] = g?.name || g?.fullName || g?.guestName || "";
      base[`Guest ${i + 1} Email`] = g?.email || g?.mail || "";
      base[`Guest ${i + 1} Phone`] = g?.phone || g?.mobile || g?.contact || "";
      base[`Guest ${i + 1} CompanyName`] = g?.companyName || g?.organisation || g?.org || "";
      base[`Guest ${i + 1} Designation`] = g?.designation || g?.role || "";
    }

    return base;
  });

  // Build a separate 'Guests' sheet where each row is a guest (useful for CSV-like analysis)
  const guestsFlat = [];
  rows.forEach((b) => {
    const guestDetails = Array.isArray(b.guestDetails) ? b.guestDetails : [];
    guestDetails.forEach((g, idx) => {
      guestsFlat.push({
        "Booking Code": b.code || "",
        "Booking Time": b.time ? new Date(b.time).toISOString() : "",
        "Guest Name": g?.name || g?.fullName || g?.guestName || "",
        "Guest Email": g?.email || g?.mail || "",
        "Guest Phone": g?.phone || g?.mobile || g?.contact || "",
        "Guest Company": g?.companyName || g?.organisation || g?.org || "",
        "Guest Designation": g?.designation || g?.role || "",
      });
    });
  });

  // create workbook and add sheets
  const ws1 = XLSX.utils.json_to_sheet(bookingRows, { header: bookingHeaders });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, "Bookings");

  // add guests sheet only if we have guest rows
  if (guestsFlat.length > 0) {
    const guestHeaders = [
      "Booking Code",
      "Booking Time",
      "Booking ID",
      "Guest Index",
      "Guest Name",
      "Guest Email",
      "Guest Phone",
      "Guest Company",
      "Guest Designation",
    ];
    const ws2 = XLSX.utils.json_to_sheet(guestsFlat, { header: guestHeaders });
    XLSX.utils.book_append_sheet(wb, ws2, "Guests");
  }

  // Autosize columns (best-effort)
  const sheetNames = wb.SheetNames;
  sheetNames.forEach((name) => {
    const ws = wb.Sheets[name];
    const range = XLSX.utils.decode_range(ws['!ref'] || "A1");
    const colWidths = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxw = 10;
      for (let R = range.s.r; R <= Math.min(range.e.r, 200); ++R) {
        const cell = ws[XLSX.utils.encode_cell({ c: C, r: R })];
        if (cell && cell.v != null) {
          const l = String(cell.v).length;
          if (l > maxw) maxw = Math.min(l, 60);
        }
      }
      colWidths.push({ wch: maxw + 2 });
    }
    ws['!cols'] = colWidths;
  });

  XLSX.writeFile(wb, filename);
}

/* -------------------- Page Component (RESPONSIVE + BETTER GUESTS) -------------------- */
export default function ListYourShowEventDashboard() {
  const { id: eventId } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [stats, setStats] = useState(null);

  const [bLoading, setBLoading] = useState(true);
  const [bErr, setBErr] = useState("");
  const [bookings, setBookings] = useState([]);

  /* Exhibition flag for conditional Guests column */
  const [isExhibition, setIsExhibition] = useState(false);

  /* -------- pagination state -------- */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [pageSize, bookings.length]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((bookings?.length || 0) / pageSize)),
    [bookings, pageSize]
  );

  const paginatedBookings = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return bookings.slice(start, end);
  }, [bookings, page, pageSize]);

  const showingFrom = useMemo(() => (bookings.length === 0 ? 0 : (page - 1) * pageSize + 1), [page, pageSize, bookings.length]);
  const showingTo = useMemo(() => Math.min(page * pageSize, bookings.length), [page, pageSize, bookings.length]);

  const goFirst = () => setPage(1);
  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const goLast = () => setPage(totalPages);

  // fetch stats
  const loadStats = async () => {
    setLoading(true);
    setErr("");
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        API?.events?.eventStats && typeof API.events?.eventStats === "function"
          ? API.events.eventStats(eventId)
          : `/api/admin/events/${eventId}/stats`;

      const res = await get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      setStats(normalizeStatsResponse(res));
    } catch (e) {
      console.error("stats error:", e);
      setErr(e?.response?.data?.message || e?.message || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  // fetch bookings (+ set isExhibition)
  const loadBookings = async () => {
    setBLoading(true);
    setBErr("");
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        API?.booking?.eventBookings && typeof API.booking?.eventBookings === "function"
          ? API.booking.eventBookings(eventId)
          : `/api/admin/events/${eventId}/bookings`;

      const res = await get(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      const root = res?.data && res.success === undefined ? res.data : res;
      const eventCategory =
        root?.data?.eventCategory ||
        root?.eventCategory ||
        stats?.event?.category ||
        stats?.eventMeta?.category ||
        "";

      setIsExhibition(String(eventCategory || "").trim().toLowerCase() === "exhibition");

      setBookings(normalizeBookingsResponse(res));
    } catch (e) {
      console.error("bookings error:", e);
      setBErr(e?.response?.data?.message || e?.message || "Failed to load bookings");
    } finally {
      setBLoading(false);
    }
  };

  useEffect(() => {
    if (!eventId) return;
    loadStats();
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const totals = stats?.totals || { totalTickets: 0, sold: 0, remaining: 0, revenue: 0 };
  const categories = stats?.categories || [];
  const ev = stats?.event || { id: eventId, name: "Event" };
  const eventMeta = stats?.eventMeta || {};

  const handleExportXLSX = () => {
    exportXLSX(bookings, `bookings_${eventId}.xlsx`, 8); // guestCap default 8
  };

  // Guests modal
  const [openGuestsFor, setOpenGuestsFor] = useState(null); // booking id or null
  const activeGuests =
    openGuestsFor ? bookings.find((x) => x.id === openGuestsFor)?.guestDetails || [] : [];

  /* -------------------- render -------------------- */
  return (
    <div className="px-4 py-6 md:py-8">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-xs uppercase text-amber-700 font-semibold">Dashboard</div>
          <h1 className="text-xl md:text-2xl font-extrabold text-slate-900 dark:text-white truncate">
            {ev?.name || "Event"}{" "}
            {eventMeta?.city ? <span className="font-normal text-slate-500">— {eventMeta.city}</span> : null}
          </h1>
          <div className="text-xs md:text-sm text-slate-600 dark:text-slate-400">
            {eventMeta?.date ? formatDDMonYYYY(eventMeta.date) : ""}{" "}
            {eventMeta?.time ? `• ${eventMeta.time}` : ""}{" "}
            {eventMeta?.venueName ? `• ${eventMeta.venueName}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              loadStats();
              loadBookings();
            }}
            className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-amber-50 dark:hover:bg-amber-900/20"
          >
            Refresh
          </button>
          <Link
            to={`/listyourshow`}
            className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-gray-800"
          >
            ← Back to Event
          </Link>
          <Link
            to="/listyourshow"
            className="rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600"
          >
            All Events
          </Link>
        </div>
      </div>

      {/* KPI row */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-3 text-sm text-slate-600 dark:text-slate-400">Loading analytics…</div>
          ) : err ? (
            <div className="col-span-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">
              {err}
            </div>
          ) : (
            <>
              <KPIBlock
                label="Total Donor Passes Sold"
                primary={`${String(totals.sold).padStart(2, "0")} tickets`}
                hint={totals.soldToday ? `+${totals.soldToday} tickets sold today` : null}
              />
              <KPIBlock label="Remaining Tickets" primary={`${totals.remaining} tickets`} />
              <KPIBlock label="Donor Pass Sales" primary={inr(totals.revenue)} />
            </>
          )}
        </div>

        {/* Categories */}
        {!loading && !err && (
          <div className="overflow-hidden rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="px-4 py-3 text-sm font-semibold">Donor Pass Category Wise Sales</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-amber-50/70 dark:bg-gray-800/60 text-slate-600 dark:text-slate-300 uppercase text-xs">
                  <tr>
                    <Th className="py-2">Donor Pass Category</Th>
                    <Th className="py-2">Price Per Ticket</Th>
                    <Th className="py-2">Donor Passes Sold</Th>
                    <Th className="py-2 text-right">Donor Pass Sales</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                  {categories.length === 0 && (
                    <tr>
                      <Td colSpan={4} className="py-6 text-center text-slate-500">
                        No category data
                      </Td>
                    </tr>
                  )}
                  {categories.map((c) => {
                    const sold = Number(c.sold || 0);
                    const total = Number(c.total || 0);
                    const soldToday = Number(c.soldToday || 0);
                    return (
                      <tr
                        key={c.id || c.categoryId || c.name}
                        className="hover:bg-amber-50/40 dark:hover:bg-gray-800/30"
                      >
                        <Td className="whitespace-nowrap">{c.name || c.categoryId || "—"}</Td>
                        <Td className="whitespace-nowrap">{inr(c.price)}</Td>
                        <Td className="min-w-[220px]">
                          <div className="flex items-center gap-2">
                            <div className="w-28">
                              <ProgressBar value={sold} max={total} />
                            </div>
                            <div className="font-mono text-xs">
                              {sold}/{total}
                            </div>
                            {soldToday > 0 && (
                              <span className="ml-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                                +{soldToday} today
                              </span>
                            )}
                          </div>
                        </Td>
                        <Td className="text-right font-semibold">{inr(c.revenue)}</Td>
                      </tr>
                    );
                  })}
                </tbody>
                {categories.length > 0 && (
                  <tfoot className="bg-slate-50/70 dark:bg-gray-800/60">
                    <tr>
                      <Td className="font-semibold">Totals</Td>
                      <Td />
                      <Td className="font-semibold">
                        {sum(categories, "sold")}/{sum(categories, "total")}
                      </Td>
                      <Td className="text-right font-bold">{inr(sum(categories, "revenue"))}</Td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Bookings - responsive: table on md+, cards on small */}
        <div className="overflow-hidden rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900">
          {/* Top bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="text-sm font-semibold">
              Bookings {bLoading ? "" : <span className="text-slate-500 font-normal">• Total Bookings: {bookings.length}</span>}
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Rows per page</span>
                <select
                  className="rounded-lg border px-2 py-1 bg-white dark:bg-gray-900 text-xs"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <button
                onClick={handleExportXLSX}
                className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-amber-50 dark:hover:bg-amber-900/20"
                title="Export to Excel (.xlsx)"
              >
                Export XLSX
              </button>
              <div className="text-xs text-slate-500">Shows latest first</div>
            </div>
          </div>

          {/* Loading / error / empty */}
          {bLoading ? (
            <div className="px-4 pb-4 text-sm text-slate-600 dark:text-slate-400">Loading bookings…</div>
          ) : bErr ? (
            <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm">{bErr}</div>
          ) : bookings.length === 0 ? (
            <div className="m-4 rounded-xl border dark:border-gray-800 p-4 text-sm text-slate-500">No bookings yet.</div>
          ) : (
            <>
              {/* DESKTOP TABLE (md+) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50/70 dark:bg-gray-800/60 text-slate-600 dark:text-slate-300 uppercase text-xs">
                    <tr>
                      <Th className="py-2">Booking Code</Th>
                      <Th className="py-2">Booking Time</Th>
                      <Th className="py-2">Donor Pass Qty</Th>
                      <Th className="py-2">Amount</Th>
                      <Th className="py-2">Donor Pass Category</Th>
                      <Th className="py-2">Booking Status</Th>
                      {isExhibition && <Th className="py-2">Guests</Th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                    {paginatedBookings.map((b) => (
                      <tr key={b.id} className="hover:bg-amber-50/40 dark:hover:bg-gray-800/30">
                        <Td className="font-mono">{b.code || "—"}</Td>
                        <Td>{formatDDMonYYYY_HHMM(b.time)}</Td>
                        <Td>{b.qty}</Td>
                        <Td className="font-semibold">{inr(b.amount)}</Td>
                        <Td className="whitespace-nowrap">{b.category || "—"}</Td>
                        <Td>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                              b.status === "paid" || b.status === "confirmed"
                                ? "bg-emerald-100 text-emerald-700"
                                : b.status === "pending"
                                ? "bg-amber-100 text-amber-800"
                                : b.status === "cancelled" || b.status === "failed"
                                ? "bg-rose-100 text-rose-700"
                                : "bg-slate-200 text-slate-700"
                            )}
                          >
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                            {b.status}
                          </span>
                        </Td>

                        {isExhibition && (
                          <Td>
                            <div className="flex items-center gap-2">
                              {/* Avatar stack: show up to 3 avatars, then +N */}
                              <div className="flex -space-x-2">
                                {Array.isArray(b.guestDetails) && b.guestDetails.slice(0, 3).map((g, i) => (
                                  <div key={i} className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-gray-900 overflow-hidden bg-slate-100 dark:bg-gray-800 flex items-center justify-center text-xs font-semibold">
                                    {getInitials(g?.name || g?.fullName || g?.companyName)}
                                  </div>
                                ))}
                              </div>

                              <div className="text-xs text-slate-600 dark:text-slate-400 font-semibold">
                                {b.guestCount ?? (Array.isArray(b.guestDetails) ? b.guestDetails.length : 0)}
                              </div>

                              {b.guestDetails?.length > 0 && (
                                <button
                                  className="text-xs rounded-full border px-2 py-1 hover:bg-slate-50 dark:hover:bg-gray-800"
                                  onClick={() => setOpenGuestsFor(b.id)}
                                >
                                  View
                                </button>
                              )}
                            </div>
                          </Td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS (smaller screens) */}
              <div className="md:hidden space-y-3 px-3 pb-4">
                {paginatedBookings.map((b) => (
                  <article key={b.id} className="rounded-lg border dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-xs text-slate-700 dark:text-slate-200">{b.code || "—"}</div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">{formatDDMonYYYY_HHMM(b.time)}</div>
                        </div>

                        <div className="mt-2 flex items-start gap-3">
                          <div className="flex flex-col text-xs text-slate-600 dark:text-slate-400">
                            <span className="font-semibold text-slate-900 dark:text-white">{inr(b.amount)}</span>
                            <span className="text-[11px]">{b.qty} • {b.category || "—"}</span>
                          </div>
                        </div>
                      </div>

                      {/* status & guests */}
                      <div className="flex flex-col items-end gap-2">
                        <div>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold",
                              b.status === "paid" || b.status === "confirmed"
                                ? "bg-emerald-100 text-emerald-700"
                                : b.status === "pending"
                                ? "bg-amber-100 text-amber-800"
                                : b.status === "cancelled" || b.status === "failed"
                                ? "bg-rose-100 text-rose-700"
                                : "bg-slate-200 text-slate-700"
                            )}
                          >
                            {b.status}
                          </span>
                        </div>

                        {isExhibition && (
                          <div className="flex items-center gap-2">
                            <div className="flex -space-x-2">
                              {Array.isArray(b.guestDetails) && b.guestDetails.slice(0, 2).map((g, i) => (
                                <div key={i} className="w-7 h-7 rounded-full ring-2 ring-white dark:ring-gray-900 overflow-hidden bg-slate-100 dark:bg-gray-800 flex items-center justify-center text-xs font-semibold">
                                  {getInitials(g?.name || g?.fullName || g?.companyName)}
                                </div>
                              ))}
                            </div>

                            <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                              {b.guestCount ?? (Array.isArray(b.guestDetails) ? b.guestDetails.length : 0)}
                            </div>

                            {b.guestDetails?.length > 0 && (
                              <button
                                className="text-xs rounded-full border px-2 py-1 hover:bg-slate-50 dark:hover:bg-gray-800"
                                onClick={() => setOpenGuestsFor(b.id)}
                              >
                                View
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* pagination footer */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t dark:border-gray-800">
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  Showing <span className="font-semibold">{showingFrom}</span>–
                  <span className="font-semibold">{showingTo}</span> of{" "}
                  <span className="font-semibold">{bookings.length}</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    className="px-2 py-1 text-xs rounded-lg border hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={goFirst}
                    disabled={page <= 1}
                    aria-label="First page"
                  >
                    « First
                  </button>
                  <button
                    className="px-2 py-1 text-xs rounded-lg border hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={goPrev}
                    disabled={page <= 1}
                    aria-label="Previous page"
                  >
                    ‹ Prev
                  </button>

                  <span className="px-2 text-xs">
                    Page <span className="font-semibold">{page}</span> / {totalPages}
                  </span>

                  <button
                    className="px-2 py-1 text-xs rounded-lg border hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={goNext}
                    disabled={page >= totalPages}
                    aria-label="Next page"
                  >
                    Next ›
                  </button>
                  <button
                    className="px-2 py-1 text-xs rounded-lg border hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    onClick={goLast}
                    disabled={page >= totalPages}
                    aria-label="Last page"
                  >
                    Last »
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Guests modal (structured fields, no JSON) */}
      {isExhibition && openGuestsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpenGuestsFor(null)} />
          <div className="relative w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-900 border dark:border-gray-800 shadow-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold">Guest Details</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    // copy a CSV-like text of guests to clipboard
                    const csv = activeGuests.map((g, i) => {
                      return [
                        i + 1,
                        g?.name || g?.fullName || "",
                        g?.email || "",
                        g?.phone || "",
                        g?.companyName || "",
                        g?.designation || ""
                      ].map(x => `"${String(x || "").replace(/"/g, '""')}"`).join(",");
                    }).join("\n");
                    copyToClipboard(csv);
                  }}
                  className="text-xs rounded-full border px-2 py-1 hover:bg-slate-50 dark:hover:bg-gray-800"
                  title="Copy guests as CSV"
                >
                  Copy CSV
                </button>

                <button
                  className="text-xs rounded-full border px-2 py-1 hover:bg-slate-50 dark:hover:bg-gray-800"
                  onClick={() => setOpenGuestsFor(null)}
                >
                  Close
                </button>
              </div>
            </div>

            {activeGuests.length === 0 ? (
              <div className="text-sm text-slate-500">No guests</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-auto">
                {activeGuests.map((g, i) => (
                  <div key={i} className="rounded-lg border dark:border-gray-800 p-3 bg-slate-50 dark:bg-gray-800/40">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center text-sm font-semibold">
                        {getInitials(g?.name || g?.fullName || g?.companyName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm text-slate-900 dark:text-white">
                            {g?.name || g?.fullName || g?.companyName || `Guest #${i + 1}`}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{g?.designation || ""}</div>
                        </div>

                        {g?.companyName && <div className="text-xs text-slate-600 dark:text-slate-400">Company: {g.companyName}</div>}

                        {g?.email && (
                          <div className="mt-2 flex items-center gap-2">
                            <a href={`mailto:${g.email}`} className="text-xs text-amber-600 hover:underline">
                              {g.email}
                            </a>
                            <button
                              onClick={() => copyToClipboard(g.email)}
                              className="text-[11px] rounded border px-2 py-0.5"
                              title="Copy email"
                            >
                              Copy
                            </button>
                          </div>
                        )}

                        {g?.phone && (
                          <div className="mt-2 flex items-center gap-2">
                            <a href={`tel:${g.phone}`} className="text-xs text-amber-600 hover:underline">
                              {g.phone}
                            </a>
                            <button
                              onClick={() => copyToClipboard(g.phone)}
                              className="text-[11px] rounded border px-2 py-0.5"
                              title="Copy mobile"
                            >
                              Copy
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
